'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';

const pageSizeOptions = [10, 25, 50, 100];

type SelectPageSizeType = {
  value?: number;
  totalCount?: number;
  onChange: (value: number) => void;
};

export const SelectPageSize = ({ value = 10, totalCount, onChange }: SelectPageSizeType) => {
  return (
    <div className='flex items-center'>
      <span className='text-sm'>Line per page</span>
      <Select
        key={String(value)}
        onValueChange={(size) => onChange(Number(size))}
        defaultValue={String(value)}
        value={String(value)}
      >
        <SelectTrigger className='mx-2 w-[100px]'>
          <SelectValue placeholder='Select page size' />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {pageSizeOptions.map((obj) => (
              <SelectItem key={obj} value={String(obj)}>
                {obj}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <span className='text-sm'>of {totalCount}</span>
    </div>
  );
};

const DOTS = '...';

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
  totalSiblingCount = 5,
}: any) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + totalSiblingCount;

    /*
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    /*
      We do not want to show dots if there is only one position left
      after/before the left/right page count as that would lead to a change if our Pagination
      component size which we do not want
    */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = totalSiblingCount * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = totalSiblingCount * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return [];
  }, [totalCount, pageSize, siblingCount, currentPage, totalSiblingCount]);

  return paginationRange;
};

type PaginationType = {
  onPageChange: (page: number) => void;
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  isShowPageSize?: boolean;
  totalSiblingCount?: number;
  usePaginationRange?: boolean;
  colorScheme?: ThemeColorSchemeType;
};

const Pagination = (props: PaginationType) => {
  const {
    onPageChange,
    totalCount,
    siblingCount = 1,
    currentPage,
    pageSize = 10,
    onPageSizeChange,
    isShowPageSize = true,
    totalSiblingCount = 5,
    usePaginationRange = true,
    colorScheme,
  } = props;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
    totalSiblingCount,
    usePaginationRange,
  }) as any;

  if (totalCount === 0) {
    return null;
  }

  const onFirst = () => {
    onPageChange(1);
  };

  const onNext = () => {
    if (currentPage < lastPage) {
      const page = currentPage + 1;
      onPageChange(page);
    }
  };

  const onPrevious = () => {
    if (currentPage > 1) {
      const page = currentPage - 1;
      onPageChange(page);
    }
  };

  const onLast = () => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    onPageChange(totalPageCount);
  };

  const lastPage = paginationRange[paginationRange.length - 1];
  return (
    <div
      className={cn(`flex flex-wrap gap-4`, isShowPageSize ? 'justify-between' : 'justify-center')}
    >
      {isShowPageSize && (
        <SelectPageSize
          value={pageSize}
          onChange={(size) => {
            onPageSizeChange(size);
          }}
          totalCount={totalCount}
        />
      )}
      <div className='flex items-center space-x-2 md:space-x-2'>
        <button
          type='button'
          className={clsx('rounded-full', currentPage === 1 && 'bg-gray-100')}
          disabled={currentPage === 1}
          onClick={onFirst}
        >
          <div className='flex items-center justify-center w-8 h-8 border rounded-full border-dark-divider md:h-10 md:w-10'>
            <ChevronsLeftIcon className='w-3 h-3 md:h-4 md:w-4' />
          </div>
        </button>
        <button
          type='button'
          className={clsx('rounded-full', currentPage === 1 && 'bg-gray-100')}
          disabled={currentPage === 1}
          onClick={onPrevious}
        >
          <div className='flex items-center justify-center w-8 h-8 border rounded-full border-dark-divider md:h-10 md:w-10'>
            <ChevronLeftIcon className='w-3 h-3 md:h-4 md:w-4' />
          </div>
        </button>
        {usePaginationRange &&
          paginationRange.map((pageNumber: any) => {
            if (pageNumber === DOTS) {
              return (
                <div key={pageNumber} className='pagination-item dots'>
                  &#8230;
                </div>
              );
            }
            return (
              <button
                type='button'
                key={pageNumber}
                className={cn(
                  'cursor-pointer rounded-full',
                  pageNumber === currentPage
                    ? isEmpty(colorScheme)
                      ? 'bg-primary text-white'
                      : 'bg-[var(--bg-color)] text-[var(--text-color)]'
                    : 'bg-white'
                )}
                style={
                  {
                    '--text-color': colorScheme?.text,
                    '--bg-color': colorScheme?.background,
                  } as React.CSSProperties
                }
                onClick={() => {
                  onPageChange(pageNumber);
                }}
              >
                <div className='flex items-center justify-center w-8 h-8 text-xs border rounded-full md:h-10 md:w-10 md:text-sm'>
                  {pageNumber}
                </div>
              </button>
            );
          })}
        <button
          type='button'
          className={clsx('rounded-full', currentPage === lastPage && 'bg-gray-100')}
          disabled={currentPage === lastPage}
          onClick={onNext}
        >
          <div className='flex items-center justify-center w-8 h-8 border rounded-full border-dark-divider md:h-10 md:w-10'>
            <ChevronRightIcon className='w-3 h-3 md:h-4 md:w-4' />
          </div>
        </button>
        <button
          type='button'
          className={clsx('rounded-full ', currentPage === lastPage && 'bg-gray-100')}
          disabled={currentPage === lastPage}
          onClick={onLast}
        >
          <div className='flex items-center justify-center w-8 h-8 border rounded-full border-dark-divider md:h-10 md:w-10'>
            <ChevronsRightIcon className='w-3 h-3 text-gray-400 md:h-4 md:w-4' />
          </div>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
